import { useState } from 'react';
import { tcesToHeatmapColor, type GtpHeatmapModel } from '@/utils/buildGtpHeatmapData';
import { formatGtpPValue, formatTcesScore } from '@/utils/formatGtpList';

const CLASS_LABEL_MAX = 36;

type GenesGtpHeatmapMatrixProps = {
  heatmap: GtpHeatmapModel;
};

type TooltipState = {
  gene: string;
  className: string;
  tces: number;
  pEnrich?: number;
  pDeplete?: number;
  stars?: number;
  left: number;
  top: number;
};

function SignificanceMarks({ count, testId }: { count: number; testId?: string }) {
  if (count <= 0) return null;

  return (
    <span className="gtp-heatmap__marks" data-testid={testId} aria-hidden>
      {count === 2 ? '**' : '*'}
    </span>
  );
}

function truncateClassLabel(name: string): string {
  if (name.length <= CLASS_LABEL_MAX) return name;
  return `${name.slice(0, CLASS_LABEL_MAX - 1)}…`;
}

function directionalPValue(tces: number, pEnrich?: number, pDeplete?: number): number | undefined {
  return tces >= 0 ? pEnrich : pDeplete;
}

export function GenesGtpHeatmapMatrix({ heatmap }: GenesGtpHeatmapMatrixProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const hasSignificance = heatmap.series.some(row => row.data.some(cell => (cell.stars ?? 0) > 0));
  const tooltipPValue = tooltip
    ? formatGtpPValue(directionalPValue(tooltip.tces, tooltip.pEnrich, tooltip.pDeplete))
    : '';
  const tooltipPLabel = tooltip && tooltip.tces >= 0 ? 'p enrich' : 'p deplete';

  return (
    <div className="gtp-heatmap__matrix">
      <div className="gtp-heatmap__layout">
        <div className="gtp-heatmap__chart" data-testid="gtp-heatmap-chart">
          <div className="gtp-heatmap__ylabels">
            {heatmap.series.map(row => (
              <div key={row.id} className="gtp-heatmap__ylabel" title={row.id}>
                {truncateClassLabel(row.id)}
              </div>
            ))}
          </div>
          <div className="gtp-heatmap__columns">
            {heatmap.genes.map((gene, geneIndex) => (
              <div key={gene.id} className="gtp-heatmap__column">
                <div className="gtp-heatmap__column-track">
                  {heatmap.series.map(row => {
                    const cell = row.data[geneIndex];
                    const value = cell?.y ?? 0;
                    const starCount = cell?.stars ?? 0;
                    return (
                      <div
                        key={row.id}
                        className="gtp-heatmap__cell"
                        data-testid="gtp-heatmap-cell"
                        data-gene={gene.symbol}
                        data-class={row.id}
                        style={{
                          backgroundColor: tcesToHeatmapColor(
                            value,
                            heatmap.maxPositive,
                            heatmap.minNegative,
                          ),
                        }}
                        onMouseEnter={event => {
                          const rect = event.currentTarget.getBoundingClientRect();
                          setTooltip({
                            gene: gene.symbol,
                            className: row.id,
                            tces: value,
                            pEnrich: cell?.pEnrich,
                            pDeplete: cell?.pDeplete,
                            stars: starCount,
                            left: rect.left + rect.width / 2,
                            top: rect.top,
                          });
                        }}
                        onMouseLeave={() => setTooltip(null)}
                      >
                        <SignificanceMarks count={starCount} testId="gtp-heatmap-significance-marks" />
                      </div>
                    );
                  })}
                </div>
                <div className="gtp-heatmap__xlabel" title={gene.symbol}>
                  {gene.symbol}
                </div>
              </div>
            ))}
          </div>
        </div>
        <aside className="gtp-heatmap__legend" aria-label="TCES legend">
          <span className="gtp-heatmap__legend-title">TCES</span>
          <span className="gtp-heatmap__legend-value">{heatmap.maxPositive.toFixed(1)}</span>
          <div className="gtp-heatmap__legend-bar" />
          <span className="gtp-heatmap__legend-value">{heatmap.minNegative.toFixed(1)}</span>
        </aside>
        {tooltip ? (
          <div
            className="gda-analytics-tooltip gtp-heatmap__tooltip"
            style={{ left: tooltip.left, top: tooltip.top }}
          >
            <strong>{tooltip.gene}</strong>
            <span>{tooltip.className}</span>
            <div>TCES: {formatTcesScore(tooltip.tces) || '0'}</div>
            {tooltipPValue ? (
              <div className="gtp-heatmap__tooltip-p">
                {tooltipPLabel}: {tooltipPValue}
                <SignificanceMarks count={tooltip.stars ?? 0} />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {hasSignificance ? (
        <p className="gtp-heatmap__significance" data-testid="gtp-heatmap-significance-legend">
          * p &lt; 0.05, ** p = 0
        </p>
      ) : null}
    </div>
  );
}
